import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('ganetov-cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(() => {
    const saved = localStorage.getItem('ganetov-promo');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    localStorage.setItem('ganetov-cart', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    if (appliedPromo) {
      localStorage.setItem('ganetov-promo', JSON.stringify(appliedPromo));
    } else {
      localStorage.removeItem('ganetov-promo');
    }
  }, [appliedPromo]);

  const addToCart = (product) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId);
      return;
    }
    setCartItems(prev =>
      prev.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    setAppliedPromo(null);
  };

  const applyPromoCode = (code, discount) => {
    setAppliedPromo({ code, discount });
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
  };

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const discountAmount = appliedPromo
    ? Math.round(subtotal * (appliedPromo.discount / 100))
    : 0;

  const total = subtotal - discountAmount;

  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Prepare order data for API
  const getOrderData = (customer) => {
    return {
      items: cartItems.map(item => ({
        product_id: item.id,
        quantity: item.quantity
      })),
      promo_code: appliedPromo?.code || null,
      customer
    };
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        isCartOpen,
        setIsCartOpen,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        applyPromoCode,
        removePromoCode,
        appliedPromo,
        subtotal,
        discountAmount,
        total,
        itemCount,
        getOrderData
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
